<template>
  <div class="web3-wallet-container">
    <button 
      v-if="isConnected" 
      class="web3-wallet-button connected center"
      @click="showWalletInfo"
      @contextmenu.prevent="resetPersistentState"
      :title="tip && $t('web3.wallet_connected')"
    >
      <div class="status-light green"></div>
      <div class="wallet-info">
        <div class="wallet-type">{{ walletTypeName }}</div>
        <div class="account-address">{{ shortAccount }}</div>
      </div>
      <s-icon color="black" width="16">chevron-down</s-icon>
    </button>

    <button 
      v-else 
      class="web3-wallet-button disconnected center"
      @click="showWalletModal"
      @contextmenu.prevent="resetPersistentState"
      :disabled="isConnecting"
      :title="tip && $t('web3.connect_wallet')"
    >
      <div class="status-light red"></div>
      <div class="wallet-text">
        <s-icon v-if="isConnecting" color="black" width="16" class="spinning">cog</s-icon>
        <span v-else>
          {{ wasPreviouslyConnected ? $t('web3.reconnect_wallet') : $t('web3.connect_wallet') }}
        </span>
      </div>
    </button>

    <transition name="dropdown">
      <div v-show="showWalletDropdown" class="wallet-dropdown" v-click-outside="closeWalletDropdown">
        <div class="dropdown-header">
          <div class="wallet-status">
            <div class="status-light green"></div>
            <span>{{ $t('web3.connected') }}</span>
          </div>
          <div v-if="wasPreviouslyConnected" class="previously-connected-indicator">
            <s-icon color="green" width="16">check-circle-outline</s-icon>
            <span>{{ $t('web3.previously_connected') }}</span>
          </div>
        </div>
        <div class="dropdown-content">
          <div class="wallet-details">
            <div class="detail-item">
              <span class="label">{{ $t('web3.wallet') }}:</span>
              <span class="value">{{ walletTypeName }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ $t('web3.account') }}:</span>
              <span class="value">{{ account }}</span>
            </div>
            <div class="detail-item">
              <span class="label">{{ $t('web3.network') }}:</span>
              <span class="value">{{ networkName }}</span>
            </div>
          </div>
          <div class="dropdown-actions">
            <button class="action-button" @click="copyAddress">
              <s-icon color="black" width="16">content-copy</s-icon>
              {{ $t('web3.copy_address') }}
            </button>
            <button class="action-button disconnect" @click="disconnectWallet">
              <s-icon color="black" width="16">logout</s-icon>
              {{ $t('web3.disconnect') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMq } from "vue3-mq";

const store = useStore();
const { t } = useI18n();
const mq = useMq();

const showWalletDropdown = ref(false);
const tip = computed(() => store.state.user.tip);

const isConnected = computed(() => store.getters['web3/isConnected']);
const account = computed(() => store.getters['web3/account']);
const shortAccount = computed(() => store.getters['web3/shortAccount']);
const chainId = computed(() => store.getters['web3/chainId']);
const walletType = computed(() => store.getters['web3/walletType']);
const isConnecting = computed(() => store.getters['web3/isConnecting']);
const wasPreviouslyConnected = computed(() => store.getters['web3/wasPreviouslyConnected']);

const walletTypeName = computed(() => {
  if (walletType.value === 'metamask') return 'MetaMask';
  if (walletType.value === 'walletconnect') return 'WalletConnect';
  if (walletType.value === 'coinbase') return 'Coinbase Wallet';
  return walletType.value || '';
});

const networkName = computed(() => {
  if (!chainId.value) return '';
  const networks = {
    '0x1': 'Ethereum Mainnet',
    '0x3': 'Ropsten Testnet',
    '0x4': 'Rinkeby Testnet',
    '0x5': 'Goerli Testnet',
    '0x2a': 'Kovan Testnet',
    '0x89': 'Polygon',
    '0x38': 'BSC',
    '0x61': 'BSC Testnet',
    '0xa': 'Optimism',
    '0xa4b1': 'Arbitrum One'
  };
  return networks[chainId.value] || `Chain ID: ${chainId.value}`;
});

const showWalletModal = () => {
  store.commit('modals/setWeb3Modal', true);
};

const showWalletInfo = () => {
  showWalletDropdown.value = !showWalletDropdown.value;
};

const closeWalletDropdown = () => {
  showWalletDropdown.value = false;
};

const copyAddress = async () => {
  try {
    await navigator.clipboard.writeText(account.value);
  } catch (err) {
    console.error('Failed to copy address:', err);
  }
};

const disconnectWallet = async () => {
  await store.dispatch('web3/disconnectWallet');
  closeWalletDropdown();
};

const resetPersistentState = async () => {
  await store.dispatch('web3/resetPersistentState');
  closeWalletDropdown();
};

onMounted(() => {
  store.dispatch('web3/checkConnection');
});
</script>

<style scoped lang="less">
.web3-wallet-container {
  position: relative;
  display: flex;
  align-items: center;
}

.web3-wallet-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--shade4);
  background-color: var(--white);
  color: var(--black);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all ease 0.2s;
  min-width: 140px;
  height: 40px;
  white-space: nowrap;

  &:hover {
    background-color: var(--shade2);
    border-color: var(--shade5);
  }

  &:focus {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.connected {
    background-color: var(--shade2);
    border-color: var(--primary);
  }

  &.disconnected {
    background-color: var(--white);
    border-color: var(--shade4);
  }

  &.mobile-web3-button {
    min-width: auto;
    max-width: none;
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
    height: 48px;
    
    .wallet-info {
      align-items: center;
      text-align: center;
    }
    
    .wallet-text {
      justify-content: center;
    }
  }

  &.web3-wallet-desktop {
    @media only screen and (max-width: 576px) {
      display: none;
    }
  }
}

.status-light {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.green {
    background-color: var(--green);
    box-shadow: 0 0 6px var(--green);
  }

  &.red {
    background-color: var(--red);
    box-shadow: 0 0 6px var(--red);
  }
}

.wallet-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  min-width: 0;
}

.wallet-type {
  font-size: 12px;
  color: var(--shade6);
  font-weight: 400;
}

.account-address {
  font-size: 13px;
  color: var(--black);
  font-weight: 500;
  font-family: 'Courier New', monospace;
}

.wallet-text {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-grow: 1;
  justify-content: center;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.wallet-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background-color: var(--white);
  border: 1px solid var(--shade4);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  min-width: 280px;
  z-index: 1000;
  overflow: hidden;
}

.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--shade4);
  background-color: var(--shade2);
}

.wallet-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: var(--black);
}

.previously-connected-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--green);
  font-weight: 500;
}

.dropdown-content {
  padding: 16px;
}

.wallet-details {
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;

  .label {
    color: var(--shade6);
    font-weight: 400;
  }

  .value {
    color: var(--black);
    font-weight: 500;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.dropdown-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--shade4);
  border-radius: 8px;
  background-color: var(--white);
  color: var(--black);
  font-size: 14px;
  cursor: pointer;
  transition: all ease 0.2s;
  justify-content: center;

  &:hover {
    background-color: var(--shade2);
    border-color: var(--shade5);
  }

  &.disconnect {
    color: var(--red);
    border-color: var(--red);

    &:hover {
      background-color: var(--red);
      color: var(--white);
    }
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: all ease 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media only screen and (max-width: 576px) {
  .web3-wallet-button {
    min-width: auto;
    max-width: 120px;
    padding: 6px 8px;
    height: 36px;
    gap: 6px;
  }

  .wallet-info {
    min-width: 0;
  }

  .wallet-type {
    font-size: 11px;
  }

  .account-address {
    font-size: 11px;
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .wallet-text {
    font-size: 13px;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .wallet-dropdown {
    position: fixed;
    top: 28%;
    left: 37%;
    transform: translate(-50%, -50%);
    right: auto;
    margin: 0;
    min-width: calc(100vw - 32px);
    max-width: 320px;
    max-height: 80vh;
    overflow-y: auto;
    z-index: 10001;
  }

  .dropdown-header {
    padding: 14px;
  }

  .dropdown-content {
    padding: 14px;
  }

  .detail-item {
    font-size: 13px;
    
    .value {
      max-width: 120px;
      font-size: 12px;
    }
  }

  .action-button {
    padding: 12px 14px;
    font-size: 13px;
  }
}
</style>
